import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { QuizsetSearchFilter } from '@/api/gen/_model/quizsetSearchFilter';
import { autocompleteQuizsetTitles } from '@/api/gen/quiz/quiz.gen';
import { usePublicTitleAutocompletePayload } from '@/module/public/hooks/usePublicTitleAutocompletePayload';
import { withServiceUnavailableRetry } from '@/shared/utils/queryRetryBudget';

const TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH = 2;
const TITLE_AUTOCOMPLETE_LIMIT = 5;
const TITLE_AUTOCOMPLETE_DEBOUNCE_MS = 400;

export const useQuizsetTitleAutocomplete = (
  query: string,
  enabled: boolean,
) => {
  const trimmedQuery = query.trim();
  const [debouncedQuery, setDebouncedQuery] = useState(trimmedQuery);
  const {
    categoryFilters,
    subjectFilter,
    enabled: isFilterContextReady,
  } = usePublicTitleAutocompletePayload();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(trimmedQuery);
    }, TITLE_AUTOCOMPLETE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [trimmedQuery]);

  const requestCategoryFilters = useMemo<QuizsetSearchFilter>(() => {
    return {
      criteria: categoryFilters,
      ...subjectFilter,
    };
  }, [categoryFilters, subjectFilter]);

  const filterContextKey = useMemo(
    () => JSON.stringify(requestCategoryFilters),
    [requestCategoryFilters],
  );

  const canSearchCurrentQuery =
    enabled &&
    isFilterContextReady &&
    trimmedQuery.length >= TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH;
  const canFetch =
    enabled &&
    isFilterContextReady &&
    debouncedQuery.length >= TITLE_AUTOCOMPLETE_MIN_QUERY_LENGTH;

  const queryResult = useQuery({
    queryKey: [
      'quizsets/title-autocomplete',
      trimmedQuery,
      debouncedQuery,
      filterContextKey,
    ],
    ...withServiceUnavailableRetry({
      queryFn: ({ signal }) =>
        autocompleteQuizsetTitles(
          {
            keyword: debouncedQuery,
            limit: TITLE_AUTOCOMPLETE_LIMIT,
            categoryFilters: requestCategoryFilters,
          },
          undefined,
          signal,
        ),
      retry: false,
    }),
    enabled: canFetch && trimmedQuery === debouncedQuery,
    staleTime: 1000 * 60 * 3,
    placeholderData: previousData => previousData,
    throwOnError: false,
    meta: { ignoreError: true },
  });

  return {
    ...queryResult,
    suggestions:
      canSearchCurrentQuery &&
      trimmedQuery === debouncedQuery &&
      !queryResult.isError
        ? queryResult.data?.data ?? []
        : [],
    isAutocompleteEnabled: canSearchCurrentQuery,
  };
};
